/**
 * Validates payment calculations to prevent incorrect charges
 * @param {Object} totals - The calculated totals object
 * @param {Array} cartItems - Array of cart items
 * @param {Array} appliedCoupons - Array of applied coupons
 * @param {boolean} payWithCreditCard - Whether paying with credit card
 * @returns {Object} Validation result with isValid flag and errors array
 */
export const validatePaymentCalculations = (
  totals,
  cartItems,
  appliedCoupons,
  payWithCreditCard
) => {
  const errors = [];
  const warnings = [];

  // Validate inputs
  if (!totals || typeof totals !== "object") {
    return {
      isValid: false,
      errors: ["Invalid totals object"],
      warnings: [],
    };
  }

  if (!Array.isArray(cartItems)) {
    return {
      isValid: false,
      errors: ["Invalid cartItems array"],
      warnings: [],
    };
  }

  if (!Array.isArray(appliedCoupons)) {
    return {
      isValid: false,
      errors: ["Invalid appliedCoupons array"],
      warnings: [],
    };
  }

  // Recalculate to verify
  let calculatedSubtotal = 0;
  cartItems.forEach((item) => {
    const price = parseFloat(item.price);
    const quantity = parseInt(item.numInCart, 10);

    if (isNaN(price) || price < 0) {
      errors.push(`Invalid price for item: ${item.title}`);
    }
    if (isNaN(quantity) || quantity < 1) {
      errors.push(`Invalid quantity for item: ${item.title}`);
    }

    calculatedSubtotal += price * quantity;
  });

  let calculatedCouponSavings = 0;
  appliedCoupons.forEach((coupon) => {
    const savings = parseFloat(coupon.dollarsSaved);
    if (isNaN(savings) || savings < 0) {
      errors.push(`Invalid coupon savings: ${coupon.code}`);
    }
    calculatedCouponSavings += savings;
  });

  // Round to 2 decimal places to avoid floating point issues
  const roundToTwo = (num) => Math.round(num * 100) / 100;

  calculatedSubtotal = roundToTwo(calculatedSubtotal);
  calculatedCouponSavings = roundToTwo(calculatedCouponSavings);

  const subtotalAfterCoupons = roundToTwo(
    calculatedSubtotal - calculatedCouponSavings
  );
  const calculatedTax = roundToTwo(subtotalAfterCoupons * 0.14);
  const calculatedCredit = payWithCreditCard
    ? roundToTwo((subtotalAfterCoupons + calculatedTax) * 0.03)
    : 0;
  const calculatedTotal = roundToTwo(
    subtotalAfterCoupons + calculatedTax + calculatedCredit
  );

  // Compare calculated vs provided totals
  const tolerance = 0.01; // Allow 1 cent tolerance for rounding

  if (Math.abs(totals.subtotal - calculatedSubtotal) > tolerance) {
    errors.push(
      `Subtotal mismatch: expected ${calculatedSubtotal}, got ${totals.subtotal}`
    );
  }

  if (Math.abs(totals.couponSavings - calculatedCouponSavings) > tolerance) {
    errors.push(
      `Coupon savings mismatch: expected ${calculatedCouponSavings}, got ${totals.couponSavings}`
    );
  }

  if (Math.abs(totals.tax - calculatedTax) > tolerance) {
    errors.push(
      `Tax mismatch: expected ${calculatedTax}, got ${totals.tax}`
    );
  }

  if (payWithCreditCard) {
    if (Math.abs(totals.credit - calculatedCredit) > tolerance) {
      errors.push(
        `Credit card fee mismatch: expected ${calculatedCredit}, got ${totals.credit}`
      );
    }
  } else {
    if (totals.credit !== 0) {
      warnings.push(
        `Credit card fee should be 0 for e-transfer, got ${totals.credit}`
      );
    }
  }

  // For e-transfer, totals.total includes credit fee but shouldn't be used
  // For credit card, totals.total should match calculatedTotal
  const expectedTotal = payWithCreditCard
    ? calculatedTotal
    : roundToTwo(subtotalAfterCoupons + calculatedTax); // E-transfer: no credit fee

  const actualTotal = payWithCreditCard
    ? totals.total
    : roundToTwo(totals.subtotal - totals.couponSavings + totals.tax); // E-transfer: recalculate without credit fee

  if (Math.abs(actualTotal - expectedTotal) > tolerance) {
    errors.push(
      `Total mismatch: expected ${expectedTotal}, got ${actualTotal} (payment method: ${payWithCreditCard ? 'credit card' : 'e-transfer'})`
    );
  }

  // Validate amounts are positive
  if (totals.subtotal < 0) {
    errors.push("Subtotal cannot be negative");
  }

  if (totals.total < 0) {
    errors.push("Total cannot be negative");
  }

  if (totals.total === 0 && cartItems.length > 0) {
    warnings.push("Total is zero but cart has items");
  }

  // Note: E-transfer total validation is now handled above in the total comparison

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    calculated: {
      subtotal: calculatedSubtotal,
      couponSavings: calculatedCouponSavings,
      tax: calculatedTax,
      credit: calculatedCredit,
      total: calculatedTotal,
    },
  };
};

/**
 * Validates Stripe payment amount matches calculated total
 * @param {number} stripeAmountInCents - Amount from Stripe payment intent (in cents)
 * @param {number} calculatedTotal - Calculated total in dollars
 * @returns {Object} Validation result
 */
export const validateStripeAmount = (stripeAmountInCents, calculatedTotal) => {
  const errors = [];
  const calculatedTotalInCents = Math.round(calculatedTotal * 100);
  const difference = Math.abs(stripeAmountInCents - calculatedTotalInCents);

  if (difference > 1) {
    // Allow 1 cent difference for rounding
    errors.push(
      `Stripe amount mismatch: Stripe has ${stripeAmountInCents} cents, calculated ${calculatedTotalInCents} cents (difference: ${difference} cents)`
    );
  }

  if (stripeAmountInCents <= 0) {
    errors.push("Stripe amount must be greater than zero");
  }

  if (calculatedTotal <= 0) {
    errors.push("Calculated total must be greater than zero");
  }

  return {
    isValid: errors.length === 0,
    errors,
    stripeAmountInCents,
    calculatedTotalInCents,
    difference,
  };
};
